import React, { useContext, useEffect } from 'react';
import ServerContext from '../../../contexts/serverContext';
import './subcategory-page-view.scss';
import { useSearchContent } from '../../../components/sections/search-bar/hooks/use-search-content';
import SearchBarInput from '../../../components/sections/search-bar/search-bar-input/search-bar-input';
import { HeroTiles, ViewBanner } from '../../../components/sections';
import siteMapService from '../../services/site-map-service/site-map-service';
import { Pagination } from '../../../components/common/pagination/pagination';
import { ASTUTE, BRAND_ATTRIBUTES } from '../../support-constants';
import { FdsChevron } from '../../../components/common/chevron/fds-chevron';
import { useBbtContent } from '../browse-by-topic/hook/use-bbt-content';
import { useAppInformationContent } from '../../../components/sections/breadcrumbs/hook/use-breadcrumb-content';
import BbtCategories from '../bbt-categories/bbt-categories';
import { SlackMessages } from '../../../services/slack-message-service/SlackMessageService';
import { Topic } from '../../models/topic';
import { useParams } from 'react-router-dom';
import { useServerData } from '../../../hooks/use-server-data';
import AstuteService from '../../services/astute-service/astute-service';
import { useHelpCardsContent } from '../../../views/page-not-found-view/hooks/use-help-cards-content';
import { DiscoverabilityPageLoad } from '../../../services/shortcode-service/shortcode-service';
import { useAnalytics } from '../../../hooks/use-analytics';
import useSeoConfig from '../../../hooks/use-seo-config';
import BreadcrumbsAndViewBanner from '../breadcrumbs-and-view-banner/breadcrumbs-and-view-banner';
import { CatSubcatFilteredResponse } from '../../models/cat-subcat-filtered-response';
import { enableLanguageLinksforTH } from '../../../components/utils/syndicate-util/syndicate-util';
import {
    getBbtErrorMessageForSubCategoryPage,
    getCategoryNameFromCategoriesResponse,
    getCurrentRootFromServerContext,
    getSubCategoryNameFromCategoriesResponse,
} from '../astute-topic/util/astute-topic-util';
import { CategoryAndSubCategoryPageBreadCrumb } from './categoryAndSubCategoryPageBreadCrumb';

interface SubCategoryPageParams {
    category: string;
    subcategory: string;
}

const SubCategoryPageView = () => {
    const { category, subcategory } = useParams<SubCategoryPageParams>();
    const searchContent = useSearchContent();
    const bbtContent = useBbtContent();
    const serverContext = useContext(ServerContext);
    const helpCardsContent = useHelpCardsContent();
    const homePagetitle = useAppInformationContent();
    const currentRoot = getCurrentRootFromServerContext(serverContext);
    const domainRoot = `https://${serverContext.domain}${currentRoot}`;
    const astuteService = new AstuteService();

    const categoriesResponse = useServerData('bbtCategories', async () => {
        try {
            const {
                categories,
                filteredCategories,
                filteredSubCategories,
            }: CatSubcatFilteredResponse = await astuteService.getAllFilteredCatAndSubcat(
                serverContext.currentLanguageRegionCode,
                (BRAND_ATTRIBUTES as any)[serverContext.brand].bbt,
                serverContext.brand,
                category,
                subcategory,
                false,
                true
            );
            if (filteredSubCategories) {
                const hrefLangs = await astuteService.getHrefLangsForSubCategory(
                    filteredSubCategories.subCategoryId,
                    serverContext.brand
                );
                serverContext.hrefLangs = hrefLangs.join(' ');
                serverContext.metaDescription =
                    filteredSubCategories.subCategoryName;
                serverContext.documentTitle =
                    filteredSubCategories.subCategoryName;
            }
            return {
                filteredCategories: filteredCategories,
                filteredSubCategories: filteredSubCategories,
                categories: categories,
                error: '',
            };
        } catch (e) {
            return { error: `SubCategory Page Error: ${e}` };
        }
    })[0];
    useSeoConfig('bbt-subcategory', 'bbt-subcategory', [
        {
            fieldName: 'category',
            fieldValue: getCategoryNameFromCategoriesResponse(
                categoriesResponse
            ),
        },
        {
            fieldName: 'sub-category',
            fieldValue: getSubCategoryNameFromCategoriesResponse(
                categoriesResponse
            ),
        },
    ]);

    const redirectionForSubCategoryView = async (errorMessage: string) => {
        const categoryPath = siteMapService.getPathFromRouterConfigs(
            serverContext.currentLanguageRegionCode,
            serverContext.brand,
            'CategoryPageView',
            [category]
        );
        if (errorMessage === SlackMessages.BBT_CONTENT_DISABLED) {
            window.location.href = `${currentRoot}/`;
        } else if (
            errorMessage === SlackMessages.SUBCATEGORY_PAGE_INVALID_URL
        ) {
            window.location.href = `${currentRoot}${categoryPath}/`;
        } else {
            window.location.href = `${currentRoot}#bbt`;
        }
    };

    const discoverabilityPageLoad: DiscoverabilityPageLoad = {
        category: category?.replaceAll('-', ' '),
        subcategory: subcategory?.replaceAll('-', ' '),
    };
    const [fireEvents] = useAnalytics();
    useEffect(() => {
        enableLanguageLinksforTH();
        fireEvents(
            ['self-help-subcategory'],
            undefined,
            {
                discoverabilityPageLoad: discoverabilityPageLoad,
            },
            false
        );
    }, []);

    useEffect(() => {
        const errorMessage = getBbtErrorMessageForSubCategoryPage(
            bbtContent,
            categoriesResponse
        );
        errorMessage && redirectionForSubCategoryView(errorMessage);
    }, []);

    const subCategoryBreadcrumbScript =
        bbtContent &&
        !bbtContent.hideBrowseByTopic &&
        `<script type="application/ld+json">{"@context": "https://schema.org","@type": "BreadcrumbList","itemListElement":[{"@type": "ListItem","position": 1,"item":{"@id": "${domainRoot}","name": "${homePagetitle?.applicationName}"}},{ "@type": "ListItem","position": 2,"item":{"@id": "${domainRoot}${categoriesResponse?.data?.filteredCategories?.categoryAbsoluteSeo}","name": "${categoriesResponse?.data?.filteredCategories?.categoryName}"} },{"@type": "ListItem","position": 3,"item":{"@id": "${domainRoot}${categoriesResponse?.data?.filteredSubCategories?.subCategoryAbsoluteSeo}","name": "${categoriesResponse?.data?.filteredSubCategories?.subCategoryName}"}}]}</script>`;

    const topicList =
        categoriesResponse?.data?.filteredSubCategories &&
        categoriesResponse?.data?.filteredSubCategories.allTopics?.map(
            (topic: Topic) => {
                return {
                    id: topic.topicId,
                    label: topic.topicLabel,
                    link: topic.topicAbsoluteSeo,
                };
            }
        );
    return searchContent &&
        bbtContent &&
        !bbtContent.hideBrowseByTopic &&
        categoriesResponse?.data?.filteredSubCategories ? (
        <div className="subcategorypage">
            <BreadcrumbsAndViewBanner
                currentPage={
                    categoriesResponse?.data?.filteredSubCategories
                        .subCategoryName || ''
                }
                hashId={'#bbt'}
                previousCrumb={
                    subcategory && (
                        <CategoryAndSubCategoryPageBreadCrumb
                            categoriesResponse={categoriesResponse}
                            currentRoot={currentRoot}
                        />
                    )
                }
                bbtTitle={bbtContent.title}
                bbtSubtitle={bbtContent.subTitle}
                isCatPage={true}
                searchClassName="sh-search"
            />

            <div className="browse-by-topic">
                <div
                    dangerouslySetInnerHTML={{
                        __html: subCategoryBreadcrumbScript || '',
                    }}
                />
                {categoriesResponse?.data?.categories && (
                    <BbtCategories
                        category={category}
                        subcategory={subcategory}
                        categories={categoriesResponse?.data?.categories}
                        filteredCategories={
                            categoriesResponse?.data?.filteredCategories
                        }
                        bbtContent={bbtContent}
                    />
                )}
                <div className="verticalline"></div>
                <section className="topics">
                    <div className="subcategory-title">
                        <div className="mobile-title">
                            <a
                                className="title-link"
                                href={`${currentRoot}${categoriesResponse?.data?.filteredCategories?.categoryAbsoluteSeo}`}
                            >
                                <FdsChevron
                                    className="chevron"
                                    type="unfilled"
                                    direction="left"
                                ></FdsChevron>

                                <h2 className="right-panel-title">
                                    {categoriesResponse?.data?.filteredSubCategories?.subCategoryName.toUpperCase()}
                                </h2>
                            </a>
                        </div>
                        <h2 className="desktop-title">
                            {categoriesResponse?.data?.filteredSubCategories?.subCategoryName.toUpperCase()}
                        </h2>
                    </div>
                    <Pagination
                        data={topicList}
                        itemsPerPage={ASTUTE.PAGINATION_ITEMS_PER_PAGE}
                    />
                </section>
            </div>

            <div className="escape-hatch">
                <ViewBanner
                    topComponent={
                        <SearchBarInput
                            hideSearchBarTitle={false}
                            searchContent={searchContent}
                        />
                    }
                    bottomComponent={
                        <HeroTiles helpCardsContent={helpCardsContent} />
                    }
                    backgroundImageDesktop={
                        process.env.REACT_APP_AEM_BASE_URL +
                        searchContent.searchBackgroundImageDesktop
                    }
                    backgroundImageMobile={
                        process.env.REACT_APP_AEM_BASE_URL +
                        searchContent.searchBackgroundImageMobile
                    }
                />
            </div>
        </div>
    ) : (
        <></>
    );
};

export default SubCategoryPageView;
